'use client';

import { c as _c } from "react/compiler-runtime";
import { useRef } from 'react';
const MOOD_EMOJIS = [{
  min: 0.9,
  emoji: '🤩'
}, {
  min: 0.7,
  emoji: '😄'
}, {
  min: 0.45,
  emoji: '😊'
}, {
  min: 0.2,
  emoji: '🙂'
}, {
  min: 0,
  emoji: '😐'
}] as const;

/** Rocket flies right along a straight track; in the last stretch it flips
 *  upright and settles onto the moon instead of nose-diving into it. */
const LAND_START = 0.85; // progress where the rocket starts flipping upright
const LANDING_LIFT = 24; // px the rocket rises to stand on the moon's surface
const KEY_STEP = 0.05; // arrow-key nudge

const RAINBOW_GRADIENT = 'linear-gradient(90deg, #f87171 0%, #fb923c 20%, #facc15 40%, #4ade80 60%, #60a5fa 80%, #c084fc 100%)';
const clamp01 = (t: number) => Math.min(1, Math.max(0, t));
export const getDonationMood = (amount: number, min: number, happyThreshold: number, max: number) => {
  const range = happyThreshold - min;
  const topRange = max - happyThreshold;
  if (range <= 0 || topRange <= 0) return 0;
  if (amount <= happyThreshold) {
    return Math.max(0, (amount - min) / range * 0.7);
  }
  return Math.min(1, 0.7 + (amount - happyThreshold) / topRange * 0.3);
};

/** Inverse of getDonationMood — turn a rocket-drag progress back into an amount. */
export const getAmountForMood = (mood: number, min: number, happyThreshold: number, max: number) => {
  const m = clamp01(mood);
  if (m <= 0.7) return min + m / 0.7 * (happyThreshold - min);
  return happyThreshold + (m - 0.7) / 0.3 * (max - happyThreshold);
};
export const getMoodMessage = (mood: number) => {
  if (mood < 0.2) return 'Meh... but every bit still helps.';
  if (mood < 0.5) return "Okay, that's actually really nice!";
  if (mood < 0.75) return "You're making our day!";
  return 'SUPER happy — thank you!!';
};
const getMoodEmoji = (mood: number) => MOOD_EMOJIS.find(step => mood >= step.min)?.emoji ?? '😐';
const RocketShape = t0 => {
  const $ = _c(9);
  const {
    flame
  } = t0;
  let t1;
  if ($[0] !== flame) {
    t1 = flame > 0 && <ellipse cx="5" cy="26" rx="3.2" ry={4 + flame * 5} fill="#fb923c" opacity={0.85} />;
    $[0] = flame;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <rect x="0" y="9" width="10" height="14" rx="2.5" fill="#e5e7eb" />;
    t3 = <path d="M 0 9 L 5 0 L 10 9 Z" fill="#f87171" />;
    t4 = <circle cx="5" cy="15" r="2.4" fill="#60a5fa" />;
    t5 = <path d="M 0 22 L -4 27 L 0 27 Z" fill="#9ca3af" />;
    t6 = <path d="M 10 22 L 14 27 L 10 27 Z" fill="#9ca3af" />;
    $[2] = t2;
    $[3] = t3;
    $[4] = t4;
    $[5] = t5;
    $[6] = t6;
  } else {
    t2 = $[2];
    t3 = $[3];
    t4 = $[4];
    t5 = $[5];
    t6 = $[6];
  }
  let t7;
  if ($[7] !== t1) {
    t7 = <g transform="translate(-5, -16)">{t1}{t2}{t3}{t4}{t5}{t6}</g>;
    $[7] = t1;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  return t7;
};
type RocketSliderProps = {
  /** 0–1 position of the rocket along the track (the slider value). */
  progress: number;
  /** Reports the dragged position (0–1). When omitted the slider is read-only. */
  onScrub?: (progress: number) => void;
  ariaLabel?: string;
  /** Human-readable current value for screen readers (e.g. "20K trons"). */
  valueText?: string;
};

/**
 * A straight-line slider whose handle is a rocket. The rainbow fills in behind
 * the rocket as it climbs; the moon sits at the end. As the rocket nears the
 * end it flips from pointing-right to upright and lifts onto the moon, so the
 * top of the range reads as a landing rather than a crash.
 */
export const RocketSlider = t0 => {
  const $ = _c(53);
  const {
    progress,
    onScrub,
    ariaLabel,
    valueText
  } = t0;
  const trackRef = useRef(null);
  const draggingRef = useRef(false);
  const t = clamp01(progress);
  const flame = t <= 0.02 ? 0 : Math.min(1, 0.3 + t);
  const moonLit = t > 0.85;
  const landT = clamp01((t - LAND_START) / (1 - LAND_START));
  const rotate = 90 * (1 - landT);
  const lift = landT * LANDING_LIFT;
  let t1;
  if ($[0] !== onScrub) {
    t1 = clientX => {
      const track = trackRef.current;
      if (!track || !onScrub) {
        return;
      }
      const rect = track.getBoundingClientRect();
      if (!rect.width) {
        return;
      }
      onScrub(clamp01((clientX - rect.left) / rect.width));
    };
    $[0] = onScrub;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const scrubToPointer = t1;
  const t2 = `relative h-16 w-full rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-primary ${onScrub ? "cursor-pointer touch-none" : ""}`;
  let t3;
  if ($[2] !== t) {
    t3 = Math.round(t * 100);
    $[2] = t;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const t4 = onScrub ? 0 : undefined;
  let t5;
  if ($[4] !== onScrub || $[5] !== scrubToPointer) {
    t5 = onScrub ? e => {
      draggingRef.current = true;
      e.currentTarget.setPointerCapture(e.pointerId);
      scrubToPointer(e.clientX);
    } : undefined;
    $[4] = onScrub;
    $[5] = scrubToPointer;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== onScrub || $[8] !== scrubToPointer) {
    t6 = onScrub ? e_0 => {
      if (draggingRef.current) {
        scrubToPointer(e_0.clientX);
      }
    } : undefined;
    $[7] = onScrub;
    $[8] = scrubToPointer;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== onScrub) {
    t7 = onScrub ? () => {
      draggingRef.current = false;
    } : undefined;
    $[10] = onScrub;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== onScrub) {
    t8 = onScrub ? () => {
      draggingRef.current = false;
    } : undefined;
    $[12] = onScrub;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] !== onScrub || $[15] !== t) {
    t9 = onScrub ? e_1 => {
      let next;
      if (e_1.key === "ArrowRight" || e_1.key === "ArrowUp") {
        next = clamp01(t + KEY_STEP);
      } else {
        if (e_1.key === "ArrowLeft" || e_1.key === "ArrowDown") {
          next = clamp01(t - KEY_STEP);
        } else {
          if (e_1.key === "Home") {
            next = 0;
          } else {
            if (e_1.key === "End") {
              next = 1;
            } else {
              return;
            }
          }
        }
      }
      e_1.preventDefault();
      onScrub(next);
    } : undefined;
    $[14] = onScrub;
    $[15] = t;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  const t10 = `${t * 100}%`;
  let t11;
  if ($[17] !== t10) {
    t11 = <div className="absolute inset-y-0 left-0 rounded-full transition-[width] duration-300 ease-out" style={{
      background: RAINBOW_GRADIENT,
      width: t10
    }} aria-hidden={true} />;
    $[17] = t10;
    $[18] = t11;
  } else {
    t11 = $[18];
  }
  let t12;
  let t13;
  let t14;
  let t15;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <div className="h-7 w-7 rounded-full bg-[#e2e8f0]" />;
    t13 = <div className="absolute left-1.5 top-2 h-1.5 w-1.5 rounded-full bg-[#cbd5e1]" />;
    t14 = <div className="absolute right-1.5 top-4 h-1 w-1 rounded-full bg-[#cbd5e1]" />;
    t15 = <div className="absolute right-2.5 top-1.5 h-1 w-1 rounded-full bg-[#cbd5e1]" />;
    $[19] = t12;
    $[20] = t13;
    $[21] = t14;
    $[22] = t15;
  } else {
    t12 = $[19];
    t13 = $[20];
    t14 = $[21];
    t15 = $[22];
  }
  const t16 = `duration-[400ms] absolute -inset-1 rounded-full border-2 border-[#facc15] transition-opacity ${moonLit ? "opacity-60" : "opacity-0"}`;
  let t17;
  if ($[23] !== t16) {
    t17 = <div className="absolute left-full top-1/2 -translate-x-1/2 -translate-y-1/2" aria-hidden={true}><div className="relative h-7 w-7">{t12}{t13}{t14}{t15}<div className={t16} /></div></div>;
    $[23] = t16;
    $[24] = t17;
  } else {
    t17 = $[24];
  }
  const t18 = `${t * 100}%`;
  let t19;
  if ($[25] !== t18) {
    t19 = {
      left: t18
    };
    $[25] = t18;
    $[26] = t19;
  } else {
    t19 = $[26];
  }
  const t20 = `translateY(${-lift}px) rotate(${rotate}deg)`;
  let t21;
  if ($[27] !== t20) {
    t21 = {
      transform: t20
    };
    $[27] = t20;
    $[28] = t21;
  } else {
    t21 = $[28];
  }
  let t22;
  if ($[29] !== flame) {
    t22 = <svg width="22" height="34" viewBox="-11 -18 22 34" className="overflow-visible"><RocketShape flame={flame} /></svg>;
    $[29] = flame;
    $[30] = t22;
  } else {
    t22 = $[30];
  }
  let t23;
  if ($[31] !== t21 || $[32] !== t22) {
    t23 = <div className="transition-transform duration-300 ease-out" style={t21}>{t22}</div>;
    $[31] = t21;
    $[32] = t22;
    $[33] = t23;
  } else {
    t23 = $[33];
  }
  let t24;
  if ($[34] !== t19 || $[35] !== t23) {
    t24 = <div className="absolute top-1/2 -translate-x-1/2 -translate-y-1/2 transition-[left] duration-300 ease-out" style={t19} aria-hidden={true}>{t23}</div>;
    $[34] = t19;
    $[35] = t23;
    $[36] = t24;
  } else {
    t24 = $[36];
  }
  let t25;
  if ($[37] !== t11 || $[38] !== t17 || $[39] !== t24) {
    t25 = <div ref={trackRef} className="absolute inset-x-7 top-1/2 h-1.5 -translate-y-1/2 rounded-full bg-textSecondary/25">{t11}{t17}{t24}</div>;
    $[37] = t11;
    $[38] = t17;
    $[39] = t24;
    $[40] = t25;
  } else {
    t25 = $[40];
  }
  let t26;
  if ($[41] !== ariaLabel || $[42] !== t2 || $[43] !== t25 || $[44] !== t3 || $[45] !== t4 || $[46] !== t5 || $[47] !== t6 || $[48] !== t7 || $[49] !== t8 || $[50] !== t9 || $[51] !== valueText) {
    t26 = <div className={t2} role="slider" aria-label={ariaLabel} aria-valuemin={0} aria-valuemax={100} aria-valuenow={t3} aria-valuetext={valueText} tabIndex={t4} onPointerDown={t5} onPointerMove={t6} onPointerUp={t7} onPointerCancel={t8} onKeyDown={t9}>{t25}</div>;
    $[41] = ariaLabel;
    $[42] = t2;
    $[43] = t25;
    $[44] = t3;
    $[45] = t4;
    $[46] = t5;
    $[47] = t6;
    $[48] = t7;
    $[49] = t8;
    $[50] = t9;
    $[51] = valueText;
    $[52] = t26;
  } else {
    t26 = $[52];
  }
  return t26;
};
type DonationAstronautProps = {
  mood: number;
};

/** The astronaut whose face reflects how generous the donation is. */
const DonationAstronaut = t0 => {
  const $ = _c(25);
  const {
    mood
  } = t0;
  let happiness;
  let t1;
  if ($[0] !== mood) {
    happiness = clamp01(mood);
    t1 = getMoodEmoji(happiness);
    $[0] = mood;
    $[1] = happiness;
    $[2] = t1;
  } else {
    happiness = $[1];
    t1 = $[2];
  }
  const emoji = t1;
  const isEcstatic = happiness >= 0.9;
  const t2 = `relative h-[7.5rem] w-[6.5rem] shrink-0 ${isEcstatic ? "animate-astronaut-bob-fast" : "animate-astronaut-bob"}`;
  let t3;
  if ($[3] !== isEcstatic) {
    t3 = isEcstatic && <span className="absolute left-0 top-[36%] text-sm" aria-hidden={true}>✨</span>;
    $[3] = isEcstatic;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <div className="absolute left-[16%] top-[10%] flex flex-col items-center" aria-hidden={true}><div className="h-1.5 w-1.5 rounded-full bg-[#c3c3c3]" /><div className="h-5 w-0.5 rounded bg-[#c3c3c3]" /><div className="h-4 w-3 rounded-sm bg-[#c3c3c3]" /></div>;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  let t6;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <div className="absolute right-[16%] top-[10%] flex flex-col items-center" aria-hidden={true}><div className="h-1.5 w-1.5 rounded-full bg-[#c3c3c3]" /><div className="h-5 w-0.5 rounded bg-[#c3c3c3]" /><div className="h-4 w-3 rounded-sm bg-[#c3c3c3]" /></div>;
    t6 = <div className="absolute left-1/2 top-[34%] h-16 w-20 -translate-x-1/2 rounded-lg bg-[#e3e3e3] shadow-sm" aria-hidden={true} />;
    $[6] = t5;
    $[7] = t6;
  } else {
    t5 = $[6];
    t6 = $[7];
  }
  let t7;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <div className="absolute right-1.5 top-1.5 h-2 w-2 rounded-full bg-white/15" />;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  const t8 = `Donation mood: ${emoji}`;
  let t9;
  if ($[9] !== emoji || $[10] !== t8) {
    t9 = <div className="absolute left-1/2 top-[6%] z-10 flex h-[4.5rem] w-[4.5rem] -translate-x-1/2 items-center justify-center rounded-full bg-white shadow-md"><div className="relative flex h-12 w-12 items-center justify-center overflow-hidden rounded-xl bg-[#00202e]">{t7}<span key={emoji} className="anim-enter absolute inset-0 flex select-none items-center justify-center text-3xl leading-none [--enter-duration:120ms] [--enter-ease:cubic-bezier(0,0,0.2,1)] [--enter-scale:0.7]" role="img" aria-label={t8}>{emoji}</span></div></div>;
    $[9] = emoji;
    $[10] = t8;
    $[11] = t9;
  } else {
    t9 = $[11];
  }
  let t10;
  let t11;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <div className="absolute left-1/2 top-[48%] z-[1] h-12 w-14 -translate-x-1/2 rounded-xl bg-[#c3c3c3] shadow-sm" aria-hidden={true} />;
    t11 = {
      transformOrigin: "top center"
    };
    $[12] = t10;
    $[13] = t11;
  } else {
    t10 = $[12];
    t11 = $[13];
  }
  const t12 = `duration-[400ms] absolute left-[14%] top-[64%] z-[2] h-7 w-3 rotate-[-20deg] rounded-full bg-[#aeb2b8] shadow-sm transition-transform ${isEcstatic ? "animate-astronaut-wave-left" : ""}`;
  let t13;
  if ($[14] !== t12) {
    t13 = <div style={t11} className={t12} aria-hidden={true} />;
    $[14] = t12;
    $[15] = t13;
  } else {
    t13 = $[15];
  }
  let t14;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = {
      transformOrigin: "top center"
    };
    $[16] = t14;
  } else {
    t14 = $[16];
  }
  const t15 = `duration-[400ms] absolute right-[14%] top-[64%] z-[2] h-7 w-3 rotate-[20deg] rounded-full bg-[#aeb2b8] shadow-sm transition-transform ${isEcstatic ? "animate-astronaut-wave-right" : ""}`;
  let t16;
  if ($[17] !== t15) {
    t16 = <div style={t14} className={t15} aria-hidden={true} />;
    $[17] = t15;
    $[18] = t16;
  } else {
    t16 = $[18];
  }
  let t17;
  if ($[19] !== t13 || $[20] !== t16 || $[21] !== t2 || $[22] !== t3 || $[23] !== t9) {
    t17 = <div className={t2}>{t3}{t4}{t5}{t6}{t9}{t10}{t13}{t16}</div>;
    $[19] = t13;
    $[20] = t16;
    $[21] = t2;
    $[22] = t3;
    $[23] = t9;
    $[24] = t17;
  } else {
    t17 = $[24];
  }
  return t17;
};
export default DonationAstronaut;